import * as React from 'react'
import { Link, withRouter } from 'react-router-dom'
import { UserContext } from './Context/User'
import { StoresContext } from './Context/Stores'
import { getCookie } from '../common'
import Transition from './Transition'
import SVG from './SVG'
import StoreMenu from './StoreMenu'

const Header = ({ history }) => {
  const user = React.useContext(UserContext)
  const storesCtx = React.useContext(StoresContext)

  React.useEffect(() => {
    if (!user.authenticated && getCookie('canRefresh')) {
      user.login()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    if (user.authenticated) {
      storesCtx.getStores()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.authenticated])

  const storeMenuButtonRef = React.useRef(null)
  const storeMenuRef = React.useRef(null)
  const [storeMenuOpen, setStoreMenuOpen] = React.useState(false)

  const toggleStoreMenu = (event) => {
    setStoreMenuOpen(!storeMenuOpen)
  }

  const mobileMenuRef = React.useRef(null)
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false)
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen)
  }

  const stripePortal = async () => {
    const result = await user.stripePortal()
    if (result.redirect) {
      window.location = result.redirect
    }
  }

  return (
    <nav className="bg-indigo-600">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center">
            <Link to="/" className="flex items-center flex-shrink-0">
              <SVG name="logo" className="w-[140px] text-indigo-300" />
            </Link>
            <div className="hidden sm:block">
              <div className="ml-10 flex items-baseline">
                <Link
                  to="/"
                  className="ml-4 px-3 py-2 rounded-md text-sm font-semibold text-indigo-200 hover:text-white hover:bg-indigo-700 focus:outline-none focus:text-white focus:bg-indigo-900"
                >
                  Home
                </Link>
                <Link
                  to="/tools"
                  className="ml-4 px-3 py-2 rounded-md text-sm font-semibold text-indigo-200 hover:text-white hover:bg-indigo-700 focus:outline-none focus:text-white focus:bg-indigo-900"
                >
                  Tools
                </Link>
                <Link
                  to="/pricing"
                  className="ml-4 px-3 py-2 rounded-md text-sm font-semibold text-indigo-200 hover:text-white hover:bg-indigo-700 focus:outline-none focus:text-white focus:bg-indigo-900"
                >
                  Pricing
                </Link>
              </div>
            </div>
          </div>
          <div className="hidden sm:block">
            <div className="ml-4 flex items-center sm:ml-6">
              {/* Profile dropdown */}
              <div className="ml-3 relative">
                <div>
                  {user.authenticated ? (
                    <div className="max-w-xs flex items-center">
                      <button
                        ref={storeMenuButtonRef}
                        onClick={toggleStoreMenu}
                        className="pr-4 text-lg text-white flex items-center gap-1.5 group"
                        id="store-menu"
                        aria-label="Store menu"
                        aria-haspopup="true"
                      >
                        <div className="bg-white rounded-full p-1">
                          <img
                            src={`https://s2.googleusercontent.com/s2/favicons?domain_url=${storesCtx.channel?.site?.url}`}
                            alt={
                              storesCtx.channel?.name || storesCtx.store.bc_name
                            }
                            className="w-5 h-5"
                          />
                        </div>
                        {/* storesCtx.store.bc_name */}

                        {storesCtx.channel?.name || storesCtx.store.bc_name}
                      </button>
                    </div>
                  ) : (
                    <Link
                      to="/login"
                      className="inline-flex items-center justify-center px-5 py-2 border border-transparent text-sm font-medium rounded-md text-indigo-600 hover:text-white bg-white hover:bg-indigo-700 transition"
                    >
                      Sign in
                    </Link>
                  )}
                </div>

                <Transition
                  buttonEl={storeMenuButtonRef?.current}
                  nodeRef={storeMenuRef}
                  show={storeMenuOpen}
                  onClose={toggleStoreMenu}
                  enter="transition ease-out duration-200"
                  enterFrom='opacity-0 translate-y-1"'
                  enterTo="opacity-100 translate-y-0"
                  leave="transition ease-in duration-150"
                  leaveFrom="opacity-100 translate-y-0"
                  leaveTo="opacity-0 translate-y-1"
                >
                  <div
                    ref={storeMenuRef}
                    className="z-50 origin-top-right absolute right-0 mt-2 rounded-md shadow-lg w-80 bg-white p-3 flex flex-col gap-3"
                  >
                    <StoreMenu onClose={() => setStoreMenuOpen(false)}>
                      {user.authenticated && user.props.isSubscribed && (
                        <button
                          onClick={() => stripePortal()}
                          className="p-2 transition flex items-center gap-1 hover:text-indigo-100 sm:hover:text-indigo-500 group"
                        >
                          <SVG
                            name="price"
                            className="w-5 h-5 text-indigo-400 sm:text-gray-400 group-hover:text-white sm:group-hover:text-indigo-500"
                          />
                          Manage subscription
                        </button>
                      )}
                      <Link
                        to="/"
                        onClick={() => {
                          user.logout()
                          storesCtx.reset()
                        }}
                        className="p-2 transition flex items-center gap-1 hover:text-indigo-100 sm:hover:text-indigo-500 group"
                      >
                        <SVG
                          name="logout"
                          className="w-5 h-5 text-indigo-400 sm:text-gray-400 group-hover:text-white sm:group-hover:text-indigo-500"
                        />
                        Sign out
                      </Link>
                    </StoreMenu>
                  </div>
                </Transition>
              </div>
            </div>
          </div>
          <div className="-mr-2 flex sm:hidden">
            {!user.authenticated && (
              <Link
                to="/login"
                className="inline-flex items-center justify-center px-5 py-2 border border-transparent text-sm font-medium rounded-md text-indigo-600 hover:text-white bg-white hover:bg-indigo-700 transition"
              >
                Sign in
              </Link>
            )}
            {/* Mobile menu button */}
            <button
              onClick={toggleMobileMenu}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-indigo-900 focus:outline-none focus:bg-indigo-900 focus:text-white"
            >
              {/* Menu open: "hidden", Menu closed: "block" */}
              <svg
                className="block h-6 w-6"
                stroke="currentColor"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
              {/* Menu open: "block", Menu closed: "hidden" */}
              <svg
                className="hidden h-6 w-6"
                stroke="currentColor"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <Transition
        nodeRef={mobileMenuRef}
        show={mobileMenuOpen}
        enter="transition ease-out duration-200"
        enterFrom='opacity-0 translate-y-1"'
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-1"
      >
        <div ref={mobileMenuRef} className="sm:hidden">
          <div className="px-2 pt-2 pb-3 sm:px-3 flex flex-col gap-0.5">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2 rounded-md text-base font-medium bg-indigo-600 focus:outline-none focus:text-white focus:bg-indigo-900 hover:bg-indigo-700 sm:hover:bg-indigo-500 hover:text-white text-white sm:text-black"
            >
              Home
            </Link>
            <Link
              to="/tools"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2 rounded-md text-base font-medium bg-indigo-600 focus:outline-none focus:text-white focus:bg-indigo-900 hover:bg-indigo-700 sm:hover:bg-indigo-500 hover:text-white text-white sm:text-black"
            >
              Tools
            </Link>
          </div>

          {user.authenticated && (
            <div className="flex flex-col gap-3 px-3 mb-3">
              <StoreMenu onClose={() => setStoreMenuOpen(false)}>
                {user.authenticated && user.props.isSubscribed && (
                  <button
                    onClick={() => stripePortal()}
                    className="p-2 transition flex items-center gap-1 hover:text-indigo-500 group"
                  >
                    <SVG
                      name="price"
                      className="w-5 h-5 text-indigo-400 sm:text-gray-400 group-hover:text-indigo-500"
                    />
                    Manage subscription
                  </button>
                )}
                <Link
                  to="/"
                  onClick={() => {
                    user.logout()
                    storesCtx.reset()
                  }}
                  className="p-2 transition flex items-center gap-1 hover:text-indigo-500 group"
                >
                  <SVG
                    name="logout"
                    className="w-5 h-5 text-indigo-400 sm:text-gray-400 sm:group-hover:text-indigo-500"
                  />
                  Sign out
                </Link>
              </StoreMenu>
            </div>
          )}
        </div>
      </Transition>
    </nav>
  )
}

export default withRouter(Header)
